import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Sparkle, CheckCircle, Circle, Camera, BellRinging, ArrowRight, Receipt, Storefront,
} from '@phosphor-icons/react';

/**
 * "Shopping Lists" landing promo — role-aware section for Home / HowItWorks / funnels.
 * Props: role: "contractor" | "worker" (defaults to contractor pitch)
 */
const ShoppingListPromo = ({ role = 'contractor' }) => {
  const navigate = useNavigate();
  const isWorker = role === 'worker';

  const copy = isWorker
    ? {
        eyebrow: 'For Workers · New',
        headline: 'Store runs without the phone tag.',
        subhead: 'Your contractor sends the exact list to your phone. Check items off in the aisle, log what you paid, snap the receipt — done.',
        cta: 'Start Getting Work',
        ctaRoute: '/join-as-worker',
        bullets: [
          { Icon: CheckCircle, text: 'Tap-to-check items at the store' },
          { Icon: Receipt, text: 'Log the price paid per item' },
          { Icon: Camera, text: 'Snap the receipt — instant proof' },
          { Icon: BellRinging, text: 'Contractor notified when you finish' },
        ],
      }
    : {
        eyebrow: 'For Contractors · New',
        headline: 'Send a worker to the store. Get exactly what you need.',
        subhead: 'Type the job — AI drafts the full materials list with quantities and prices. Send it to a worker, watch items get checked off live, and get the receipt when they finish.',
        cta: 'Sign Up Free',
        ctaRoute: '/become-a-contractor',
        bullets: [
          { Icon: Sparkle, text: 'AI drafts the list from a job description' },
          { Icon: CheckCircle, text: 'Watch items get picked in real time' },
          { Icon: Receipt, text: 'Actual spend vs. estimate, per item' },
          { Icon: Camera, text: 'Receipt photo attached — no lost paper' },
        ],
      };

  return (
    <section className="py-24 bg-[#134E4A] text-white overflow-hidden relative" data-testid={`shopping-list-promo-${role}`}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.15) 40px, rgba(255,255,255,0.15) 41px)' }} />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border-2 border-[#10B981] px-4 py-2 mb-6">
              <ShoppingCart size={16} weight="fill" className="text-[#34D399]" />
              <span className="text-sm font-bold text-[#34D399] uppercase tracking-widest">{copy.eyebrow}</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4 uppercase tracking-tight leading-tight">
              Smart Shopping<br /><span className="text-[#34D399]">Lists</span>
            </h2>
            <p className="text-lg text-emerald-100/80 mb-8 max-w-xl">{copy.subhead}</p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {copy.bullets.map(({ Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3 bg-white/10 border-2 border-white/20 p-3 rounded-xl" data-testid={`shopping-promo-bullet-${i}`}>
                  <div className="w-9 h-9 flex-shrink-0 bg-[#10B981] rounded-lg flex items-center justify-center">
                    <Icon size={18} weight="bold" className="text-white" />
                  </div>
                  <span className="text-sm font-bold leading-snug text-white">{text}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate(copy.ctaRoute)}
              className="inline-flex items-center gap-2 h-14 px-8 bg-[#10B981] hover:bg-[#059669] text-white font-black uppercase text-sm tracking-widest border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none rounded-none transition-all"
              data-testid={`shopping-promo-cta-${role}`}
            >
              {copy.cta} <ArrowRight size={16} weight="bold" />
            </button>
          </div>

          {/* Phone mockup */}
          <div className="lg:col-span-2">
            <div className="bg-black/40 border-4 border-[#10B981] shadow-[8px_8px_0px_rgba(16,185,129,0.4)] p-4 md:p-5 rounded-3xl max-w-sm mx-auto">
              <div className="bg-white rounded-2xl p-4 text-neutral-900">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-black text-sm">Deck Framing Run</span>
                  <span className="text-[9px] font-black uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Shopping</span>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3 flex items-center gap-1">
                  <Storefront size={11} /> Home Depot · <Sparkle size={11} weight="fill" className="text-[#EA580C]" /> AI-drafted · 3/4 picked
                </p>
                <ul className="space-y-2 text-sm">
                  <MockItem done text="24 pcs — 2x4x8 PT lumber" price="6.48" />
                  <MockItem done text="2 box — 3&quot; deck screws" price="11.97" />
                  <MockItem done text="8 pcs — joist hangers" price="1.86" />
                  <MockItem text="1 tube — construction adhesive" />
                </ul>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-[#10B981]">
                    <Camera size={12} weight="bold" /> Receipt ✓
                  </span>
                  <span className="text-xs font-black">$194.33 spent</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MockItem = ({ text, done = false, price }) => (
  <li className="flex items-center gap-2">
    {done ? <CheckCircle size={18} weight="fill" className="text-[#10B981] shrink-0" /> : <Circle size={18} className="text-neutral-300 shrink-0" />}
    <span className={`flex-1 text-xs ${done ? 'line-through text-neutral-400' : 'text-neutral-800 font-bold'}`}>{text}</span>
    {price && <span className="text-[10px] font-black text-[#10B981]">${price}</span>}
  </li>
);

export default ShoppingListPromo;
